import { useState, useEffect } from 'react';
import { Card, CardHeader, CardContent } from '../../../components/Card';
import { Badge } from '../../../components/Badge';
import { Button } from '../../../components/Button';
import { useResourceStore } from '../../../store/resourceStore';

export function ResourceManagement() {
    const [activeTab, setActiveTab] = useState<'bench' | 'utilization' | 'capacity'>('bench');
    const { bench: benchResources, utilization, capacity, fetchBench, fetchUtilization, fetchCapacity, loading } = useResourceStore();

    useEffect(() => {
        fetchBench();
        fetchUtilization();
        fetchCapacity();
    }, []);

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="tracking-tight text-foreground font-display text-page-title">Resource <span className="text-foreground">Management</span></h1>
                    <p className="text-muted-foreground mt-1">Bench tracking, utilization, and capacity planning</p>
                </div>
                <div className="flex gap-3">
                    <Button variant="secondary">Request Resource</Button>
                    <Button variant="primary">Add to Bench</Button>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 border-b border-border">
                {[
                    { id: 'bench', label: 'Bench Matrix' },
                    { id: 'utilization', label: 'Utilization Heatmap' },
                    { id: 'capacity', label: 'Capacity Planning' }
                ].map(t => (
                    <Button variant="unstyled" size="unstyled"
                        key={t.id}
                        onClick={() => setActiveTab(t.id as any)}
                        className={`pb-4 px-4 text-sm font-semibold transition-all relative ${activeTab === t.id ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}
                    >
                        {t.label}
                        {activeTab === t.id && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />}
                    </Button>
                ))}
            </div>

            {/* Bench View */}
            {activeTab === 'bench' && (
                <Card>
                    <CardHeader className="bg-card/30 px-6 py-4">
                        <h2 className="text-foreground font-display text-section-title">Available Resources ({benchResources.length})</h2>
                    </CardHeader>
                    <CardContent className="p-0">
                        {loading && <div className="p-8 text-center text-muted-foreground">Loading bench...</div>}
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-sm text-muted-foreground font-semibold bg-card/50 border-b border-border">
                                    <th className="text-left px-6 py-4">Professional</th>
                                    <th className="text-left px-4 py-4">Core Skills</th>
                                    <th className="text-center px-4 py-4">Available Since</th>
                                    <th className="text-center px-4 py-4">Status</th>
                                    <th className="text-right px-6 py-4">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border-12">
                                {benchResources.map((res, i) => (
                                    <tr key={i} className="hover:bg-card/30 transition-colors group">
                                        <td className="px-6 py-4">
                                            <p className="font-semibold text-foreground">{res.name}</p>
                                            <p className="text-xs text-muted-foreground">{res.role}</p>
                                        </td>
                                        <td className="px-4 py-4">
                                            <div className="flex flex-wrap gap-1">
                                                {res.skills.map(skill => (
                                                    <span key={skill} className="px-2 py-0.5 text-xs font-semibold bg-card text-muted-foreground rounded-lg">{skill}</span>
                                                ))}
                                            </div>
                                        </td>
                                        <td className="px-4 py-4 text-center text-muted-foreground">{new Date(res.available_since).toLocaleDateString()}</td>
                                        <td className="px-4 py-4 text-center">
                                            <Badge variant="success">{res.status}</Badge>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <Button variant="secondary" size="sm" className="opacity-0 group-hover:opacity-100 transition-opacity text-xs">Assign</Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </CardContent>
                </Card>
            )}

            {/* Utilization Heatmap */}
            {activeTab === 'utilization' && (
                <Card>
                    <CardHeader className="bg-card/30 px-6 py-4 flex justify-between items-center flex-row">
                        <h2 className="text-foreground font-display text-section-title">Weekly Utilization (Heatmap)</h2>
                        <div className="flex items-center gap-4 text-xs font-semibold text-muted-foreground">
                            <div className="flex items-center gap-1.5"><div className="w-3 h-3 bg-card border border-border rounded-sm"></div> 0%</div>
                            <div className="flex items-center gap-1.5"><div className="w-3 h-3 bg-warning/80 rounded-sm"></div> 1-99%</div>
                            <div className="flex items-center gap-1.5"><div className="w-3 h-3 bg-success rounded-sm"></div> 100%</div>
                            <div className="flex items-center gap-1.5"><div className="w-3 h-3 bg-destructive rounded-sm"></div> &gt;100%</div>
                        </div>
                    </CardHeader>
                    <CardContent className="p-0 overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-sm text-muted-foreground font-semibold bg-card/50 border-b border-border">
                                    <th className="text-left px-6 py-4 w-64 border-r border-border">Resource</th>
                                    {['Mon 10', 'Tue 11', 'Wed 12', 'Thu 13', 'Fri 14'].map(day => (
                                        <th key={day} className="text-center px-4 py-4 w-24">{day}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border-12">
                                {utilization.length > 0 ? utilization.map((res, i) => {
                                    return (
                                        <tr key={i} className="hover:bg-card/30 transition-colors">
                                            <td className="px-6 py-4 border-r border-border">
                                                <p className="font-semibold text-foreground">{res.project_name}</p>
                                                <p className="text-xs text-muted-foreground">{res.member_count} active members</p>
                                            </td>
                                            {/* Since the real backend doesn't provide daily util yet, we can render 
                                                a block based on member count as a placeholder for heatmap visuals, 
                                                but without using random data */}
                                            {[1, 2, 3, 4, 5].map((_, j) => {
                                                const util = Math.min(100, res.member_count * 20); // deterministic util based on members
                                                return (
                                                <td key={j} className="p-2 text-center">
                                                    <div className={`w-full h-10 rounded-sm flex items-center justify-center text-xs font-semibold
                                                        ${util === 0 ? 'bg-card border border-border text-muted-foreground' :
                                                            util < 100 ? 'bg-warning/20 text-warning border border-warning/30' :
                                                                util === 100 ? 'bg-success/20 text-success border border-success/30' :
                                                                    'bg-destructive/20 text-destructive border border-destructive/30'
                                                        }`}>
                                                        {util}%
                                                    </div>
                                                </td>
                                            )})}
                                        </tr>
                                    );
                                }) : (
                                    <tr><td colSpan={6} className="text-center p-8 text-muted-foreground">No utilization data available</td></tr>
                                )}
                            </tbody>
                        </table>
                    </CardContent>
                </Card>
            )}

            {/* Capacity Planning */}
            {activeTab === 'capacity' && (
                <Card>
                    <CardHeader className="bg-card/30 px-6 py-4">
                        <h2 className="text-foreground font-display text-section-title">Role Capacity vs Demand (Next 30 Days)</h2>
                    </CardHeader>
                    <CardContent className="p-0">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-sm text-muted-foreground font-semibold bg-card/50 border-b border-border">
                                    <th className="text-left px-6 py-4">Role</th>
                                    <th className="text-right px-6 py-4">Total Capacity (Hrs)</th>
                                    <th className="text-right px-6 py-4">Forecasted Demand (Hrs)</th>
                                    <th className="text-right px-6 py-4">Net Availability</th>
                                    <th className="text-center px-6 py-4">Status</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border-12">
                                {capacity.length > 0 ? capacity.map((row, i) => {
                                    const net = row.cap - row.dem;
                                    return (
                                        <tr key={i} className="hover:bg-card/30 transition-colors">
                                            <td className="px-6 py-4 font-semibold text-foreground">{row.role}</td>
                                            <td className="px-6 py-4 text-right font-mono">{row.cap}</td>
                                            <td className="px-6 py-4 text-right font-mono">{row.dem}</td>
                                            <td className={`px-6 py-4 text-right font-mono font-semibold ${net < 0 ? 'text-destructive' : net === 0 ? 'text-warning' : 'text-success'}`}>
                                                {net > 0 ? '+' : ''}{net}
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <Badge variant={net < 0 ? 'error' : net === 0 ? 'warning' : 'success'}>
                                                    {net < 0 ? 'Overbooked' : net === 0 ? 'At Capacity' : 'Available'}
                                                </Badge>
                                            </td>
                                        </tr>
                                    );
                                }) : (
                                    <tr><td colSpan={5} className="text-center p-8 text-muted-foreground">No capacity data available</td></tr>
                                )}
                            </tbody>
                        </table>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
